import React, { useState } from 'react';
import { auth, googleProvider } from '../firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup
} from 'firebase/auth';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Heading,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormErrorMessage,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Link,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';
import OssLicensesModal from './OssLicensesModal';



export default function SignUpScreen({ isInPane = false, onClose, onSwitchToLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const [isOssModalOpen, setOssModalOpen] = useState(false);

  // 利用規約テキストを初回のみ取得
  React.useEffect(() => {
    fetch('/terms.txt')
      .then((res) => res.text())
      .then((text) => setTermsText(text));
  }, []);

  // バリデーション
  const isEmailValid = email.includes('@') && email.includes('.');
  const isPasswordValid = password.length >= 6;
  const isConfirmPasswordValid = password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = isEmailValid && isPasswordValid && isConfirmPasswordValid && isAgreementChecked;

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toast({
        title: "入力エラー",
        description: "入力内容を確認してください。",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      setEmailSent(true);
      toast({
        title: "アカウント作成完了",
        description: "確認メールを送信しました。メールを確認してください。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Email SignUp Error:", error);
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "サインアップエラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Google認証
  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      toast({
        title: "Googleアカウントで登録完了",
        description: "Googleアカウントで新規登録・ログインしました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      if (isInPane && onSwitchToLogin) {
        onSwitchToLogin();
      } else {
        navigate('/equipment');
      }
    } catch (error) {
      console.error("Google SignUp Error:", error);
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "Google登録エラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSuccessContent = () => (
    <VStack spacing={6}>
      <Alert status="success">
        <AlertIcon />
        <Box>
          <AlertTitle>アカウント作成完了！</AlertTitle>
          <AlertDescription>
            {email} に確認メールを送信しました。
            <br />
            メール内のリンクをクリックして、メールアドレスの確認を完了してください。
          </AlertDescription>
        </Box>
      </Alert>
      
      <Button
        bg="black"
        color="white"
        w="full"
        onClick={isInPane ? onSwitchToLogin : () => navigate('/auth')}
      >
        ログイン画面に戻る
      </Button>
    </VStack>
  );

  if (emailSent) {
    if (isInPane) {
      return renderSuccessContent();
    }
    
    return (
      <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
            {renderSuccessContent()}
          </Box>
        </Container>
      </Box>
    );
  }

  const renderMainContent = () => (
    <VStack spacing={6}>
      <Button
        w="full"
        size="lg"
        onClick={handleGoogleSignUp}
        leftIcon={<img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" style={{ height: '18px' }} />}
        bg="black"
        color="white"
        variant="outline"
        borderColor="gray.300"
        isLoading={isLoading}
      >
        Googleで新規登録
      </Button>
      <Text fontSize="xs" color="gray.500" textAlign="center">
        Google認証を利用する場合、
        <a href="https://policies.google.com/terms?hl=ja" target="_blank" rel="noopener noreferrer">Google利用規約</a>
        および
        <a href="https://policies.google.com/privacy?hl=ja" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
        に同意したものとみなします。
      </Text>
      <Heading size="sm" color="gray.300">-----------------または-----------------</Heading>
      <Heading size="md" color="gray.700">メールアドレスで新規アカウント作成</Heading>
      <Text color="gray.600">メールアドレスとパスワードを入力してください。</Text>

      <form onSubmit={handleEmailSignUp} style={{ width: '100%' }}>
        <VStack spacing={4} w="100%">
          <FormControl isRequired isInvalid={email.length > 0 && !isEmailValid}>
            <FormLabel>メールアドレス</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
            />
            {email.length > 0 && !isEmailValid && (
              <FormErrorMessage>有効なメールアドレスを入力してください</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={password.length > 0 && !isPasswordValid}>
            <FormLabel>パスワード</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6文字以上"
                autoComplete="new-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? "パスワードを隠す" : "パスワードを表示"}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword(!showPassword)}
                  variant="ghost"
                  size="sm"
                  color="gray.500"
                />
              </InputRightElement>
            </InputGroup>
            {password.length > 0 && !isPasswordValid && (
              <FormErrorMessage>パスワードは6文字以上で入力してください</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isRequired isInvalid={confirmPassword.length > 0 && !isConfirmPasswordValid}>
            <FormLabel>パスワード確認</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="パスワードを再入力"
                autoComplete="new-password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showConfirmPassword ? "パスワードを隠す" : "パスワードを表示"}
                  icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  variant="ghost"
                  size="sm"
                  color="gray.500"
                />
              </InputRightElement>
            </InputGroup>
            {confirmPassword.length > 0 && !isConfirmPasswordValid && (
              <FormErrorMessage>パスワードが一致しません</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isRequired>
            <Checkbox
              isChecked={isAgreementChecked}
              onChange={(e) => setIsAgreementChecked(e.target.checked)}
              colorScheme="yellow"
            >
              <span>
                <Link color="yellow.600" onClick={() => setIsTermsOpen(true)} textDecoration="underline">
                  利用規約（個人情報保護方針を含む）
                </Link>
                に同意します
              </span>
            </Checkbox>
            {!isAgreementChecked && (
              <FormErrorMessage>利用規約への同意が必要です</FormErrorMessage>
            )}
          </FormControl>

          <Button
            type="submit"
            bg="black"
            color="white"
            size="lg"
            w="full"
            isLoading={isLoading}
            loadingText="作成中..."
            isDisabled={!isFormValid}
          >
            アカウントを作成
          </Button>

          <Button
            variant="link"
            colorScheme="gray"
            onClick={isInPane ? onSwitchToLogin : () => navigate('/auth')}
          >
            ログイン画面に戻る
          </Button>
        </VStack>
      </form>

      {/* 利用規約モーダル */}
      <Modal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>利用規約（個人情報保護方針を含む）</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box whiteSpace="pre-wrap" maxH="60vh" overflowY="auto" fontSize="sm">
              {termsText}
            </Box>
            <Button mt={4} variant="link" colorScheme="gray" size="sm" onClick={() => setOssModalOpen(true)}>
              OSSライセンス一覧はこちら
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsTermsOpen(false)} colorScheme="yellow">閉じる</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <OssLicensesModal isOpen={isOssModalOpen} onClose={() => setOssModalOpen(false)} />
    </VStack>
  );

  if (isInPane) {
    return renderMainContent();
  }

  return (
    <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md">
        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          {renderMainContent()}
        </Box>
      </Container>
    </Box>
  );
} 